import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { paymentsAPI, billsAPI, billCategoriesAPI } from '../services/api';
import { useToast } from '../context/ToastContext';
import { getCurrencySymbol, formatAmount, formatAmountLocale } from '../utils/formatters';
import { parseDate } from '../utils/dateUtils';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  CreditCard,
  Zap,
  ChevronLeft,
  ChevronRight,
  Activity,
  Target,
  Minus
} from 'lucide-react';
import '../styles/Stats.css';
import BillIcon from '../components/common/BillIcon';

const CHART_COLORS = [
  '#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', 
  '#ec4899', '#06b6d4', '#f97316', '#6366f1', '#84cc16'
];

function BarChart({ data, height = 200, currencySymbol }) {
  if (!data || data.length === 0) return null;
  const maxValue = Math.max(...data.map(d => d.value));
  
  return (
    <div className="bar-chart" style={{ height }}>
      {data.map((item, index) => (
        <div key={index} className="bar-column">
          <div className="bar-track">
            {item.value > 0 && (
              <div 
                className="bar-fill"
                style={{ 
                  height: `${(item.value / maxValue) * 100}%`,
                  backgroundColor: item.color || 'var(--primary-color)'
                }}
              >
                <span className="bar-value">{currencySymbol}{(item.value / 1000).toFixed(1)}k</span>
              </div>
            )}
          </div>
          <span className="bar-label">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function DonutChart({ data, size = 180, currencySymbol }) {
  if (!data || data.length === 0) return null;
  const total = data.reduce((sum, d) => sum + d.value, 0);
  let cumulative = 0;
  
  const getCoords = (percent) => {
    const x = Math.cos(2 * Math.PI * percent - Math.PI / 2);
    const y = Math.sin(2 * Math.PI * percent - Math.PI / 2);
    return [x, y];
  };
  
  return (
    <div className="donut-wrapper">
      <svg viewBox="-1.2 -1.2 2.4 2.4" width={size} height={size}>
        {data.map((item, i) => {
          if (item.value === 0) return null;
          const pct = item.value / total;
          const [sx, sy] = getCoords(cumulative);
          cumulative += pct;
          const [ex, ey] = getCoords(cumulative);
          const large = pct > 0.5 ? 1 : 0;
          return (
            <path
              key={i}
              d={`M ${sx} ${sy} A 1 1 0 ${large} 1 ${ex} ${ey} L 0 0`}
              fill={item.color}
            />
          );
        })}
        <circle r="0.6" fill="var(--surface-color)" />
        <text x="0" y="0" textAnchor="middle" dominantBaseline="middle" className="donut-total">
          {currencySymbol}{(total / 1000).toFixed(1)}k
        </text>
      </svg>
      <div className="donut-legend">
        {data.filter(d => d.value > 0).map((item, i) => (
          <div key={i} className="legend-row">
            <span className="legend-dot" style={{ backgroundColor: item.color }} />
            <span className="legend-icon">{item.icon}</span>
            <span className="legend-name">{item.label}</span>
            <span className="legend-amount">{currencySymbol}{item.value.toFixed(0)} ({((item.value / total) * 100).toFixed(1)}%)</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function Stats() {
  const { error: showError } = useToast();
  const [payments, setPayments] = useState([]);
  const [bills, setBills] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [viewMode, setViewMode] = useState('overview');

  // Get currency symbol once for the component
  const currencySymbol = getCurrencySymbol();

  const loadData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const [paymentsRes, billsRes, categoriesRes] = await Promise.all([
        paymentsAPI.getAll({ limit: 1000 }),
        billsAPI.getAll(true),
        billCategoriesAPI.getAll(false)
      ]);
      setPayments(paymentsRes.data.payments || []);
      setBills(billsRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      showError('Failed to load statistics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [showError]);

  useEffect(() => {
    loadData();
  }, [selectedYear, loadData]);

  const handleRefresh = () => {
    loadData(true);
  };

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((cat, i) => {
      map[cat.name] = { icon: cat.icon, color: CHART_COLORS[i % CHART_COLORS.length] };
    });
    return map;
  }, [categories]);

  const getCategoryInfo = useCallback((name) => categoryMap[name] || { icon: '📋', color: '#6b7280' }, [categoryMap]);

  const stats = useMemo(() => {
    const yearPayments = payments.filter(p => {
      const d = parseDate(p.payment_date);
      return d && d.getFullYear() === selectedYear;
    });
    const prevYearPayments = payments.filter(p => {
      const d = parseDate(p.payment_date);
      return d && d.getFullYear() === selectedYear - 1;
    });
    
    const monthlyTotals = Array(12).fill(0);
    yearPayments.forEach(p => {
      const d = parseDate(p.payment_date);
      if (d) monthlyTotals[d.getMonth()] += parseFloat(p.amount) || 0;
    });

    const prevMonthlyTotals = Array(12).fill(0);
    prevYearPayments.forEach(p => {
      const d = parseDate(p.payment_date);
      if (d) prevMonthlyTotals[d.getMonth()] += parseFloat(p.amount) || 0;
    });
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthlyData = monthlyTotals.map((value, i) => ({
      label: monthNames[i],
      value,
      prevValue: prevMonthlyTotals[i],
      color: value > 0 ? 'var(--primary-color)' : 'var(--border-color)'
    }));

    const categoryTotals = {};
    yearPayments.forEach(p => {
      const cat = p.category || 'Other';
      categoryTotals[cat] = (categoryTotals[cat] || 0) + (parseFloat(p.amount) || 0);
    });

    const prevCategoryTotals = {};
    prevYearPayments.forEach(p => {
      const cat = p.category || 'Other';
      prevCategoryTotals[cat] = (prevCategoryTotals[cat] || 0) + (parseFloat(p.amount) || 0);
    });
    
    const categoryData = Object.entries(categoryTotals)
      .map(([label, value]) => ({ 
        label, 
        value, 
        prevValue: prevCategoryTotals[label] || 0,
        ...getCategoryInfo(label) 
      }))
      .sort((a, b) => b.value - a.value);

    const billTotals = {};
    yearPayments.forEach(p => {
      const name = p.bill_name || 'Unknown';
      if (!billTotals[name]) {
        billTotals[name] = { name, total: 0, count: 0, category: p.category, iconUrl: p.icon_url };
      }
      billTotals[name].total += parseFloat(p.amount) || 0;
      billTotals[name].count++;
    });
    
    const topBills = Object.values(billTotals).sort((a, b) => b.total - a.total).slice(0, 10);
    const totalSpent = yearPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const prevTotalSpent = prevYearPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const monthsWithData = monthlyTotals.filter(m => m > 0).length;
    const avgMonthly = monthsWithData > 0 ? totalSpent / monthsWithData : 0;
    const prevMonthsWithData = prevMonthlyTotals.filter(m => m > 0).length;
    const prevAvgMonthly = prevMonthsWithData > 0 ? prevTotalSpent / prevMonthsWithData : 0;
    
    let autoPayCount = 0;
    yearPayments.forEach(p => {
      const bill = bills.find(b => b.id === p.bill_id);
      if (bill?.auto_pay) autoPayCount++;
    });

    // Year-over-Year comparison data
    const yoyData = {
      totalChange: prevTotalSpent > 0 ? ((totalSpent - prevTotalSpent) / prevTotalSpent) * 100 : null,
      avgChange: prevAvgMonthly > 0 ? ((avgMonthly - prevAvgMonthly) / prevAvgMonthly) * 100 : null,
      prevTotalSpent,
      prevAvgMonthly,
      prevPaymentCount: prevYearPayments.length,
      monthlyComparison: monthlyData.map((m, i) => ({
        ...m,
        change: prevMonthlyTotals[i] > 0 ? ((m.value - prevMonthlyTotals[i]) / prevMonthlyTotals[i]) * 100 : null
      })),
      categoryComparison: categoryData.map(cat => ({
        ...cat,
        change: cat.prevValue > 0 ? ((cat.value - cat.prevValue) / cat.prevValue) * 100 : null
      }))
    };

    return { monthlyData, categoryData, topBills, totalSpent, avgMonthly, autoPayCount, paymentCount: yearPayments.length, yoyData };
  }, [payments, bills, selectedYear, getCategoryInfo]);

  if (loading) {
    return (
      <div className="stats-loading">
        <Activity size={32} className="spin" />
        <span>Loading statistics...</span>
      </div>
    );
  }

  return (
    <div className="page stats-page">
      <header className="stats-header">
        <div className="stats-title-row">
          <div>
            <h1><BarChart3 size={24} /> Statistics & Analytics</h1>
            <p>Analyze your spending patterns and trends</p>
          </div>
          <div className="stats-header-actions">
            <div className="year-nav">
              <button onClick={() => setSelectedYear(y => y - 1)} disabled={selectedYear <= 2020}>
                <ChevronLeft size={18} />
              </button>
              <span>{selectedYear}</span>
              <button onClick={() => setSelectedYear(y => y + 1)} disabled={selectedYear >= new Date().getFullYear()}>
                <ChevronRight size={18} />
              </button>
            </div>
            <button className="refresh-icon-btn" onClick={handleRefresh} disabled={refreshing} title="Refresh data">
              <RefreshCw size={20} className={refreshing ? 'spin' : ''} />
            </button>
          </div>
        </div>
      </header>

      <div className="stats-cards">
        <div className="stat-card primary">
          <DollarSign size={24} />
          <div>
            <strong>{formatAmountLocale(stats.totalSpent)}</strong>
            <span>Total Spent in {selectedYear}</span>
          </div>
        </div>
        <div className="stat-card accent-blue">
          <Calendar size={24} />
          <div>
            <strong>{formatAmountLocale(stats.avgMonthly)}</strong>
            <span>Average Monthly</span>
          </div>
        </div>
        <div className="stat-card accent-green">
          <Zap size={24} />
          <div>
            <strong>{stats.autoPayCount}</strong>
            <span>Auto-Pay Transactions</span>
          </div>
        </div>
        <div className="stat-card accent-purple">
          <CreditCard size={24} />
          <div>
            <strong>{stats.paymentCount}</strong>
            <span>Total Payments</span>
          </div>
        </div>
      </div>

      <nav className="stats-tabs">
        {['overview', 'monthly', 'category', 'comparison'].map(mode => (
          <button
            key={mode}
            className={viewMode === mode ? 'active' : ''}
            onClick={() => setViewMode(mode)}
          >
            {mode === 'overview' && <Activity size={16} />}
            {mode === 'monthly' && <BarChart3 size={16} />}
            {mode === 'category' && <PieChart size={16} />}
            {mode === 'comparison' && <TrendingUp size={16} />}
            <span className="tab-label">{mode.charAt(0).toUpperCase() + mode.slice(1)}</span>
          </button>
        ))}
      </nav>

      <div className="stats-content">
        {viewMode === 'overview' && (
          <>
            <section className="panel full">
              <h3><BarChart3 size={18} /> Monthly Spending</h3>
              <div className="panel-body">
                {stats.monthlyData.some(d => d.value > 0) ? (
                  <BarChart data={stats.monthlyData} height={220} currencySymbol={currencySymbol} />
                ) : (
                  <div className="empty">No payment data for {selectedYear}</div>
                )}
              </div>
            </section>
            
            <div className="panel-row">
              <section className="panel">
                <h3><PieChart size={18} /> By Category</h3>
                <div className="panel-body">
                  {stats.categoryData.length > 0 ? (
                    <DonutChart data={stats.categoryData} currencySymbol={currencySymbol} />
                  ) : (
                    <div className="empty">No category data</div>
                  )}
                </div>
              </section>
              
              <section className="panel">
                <h3><Target size={18} /> Top Expenses</h3>
                <div className="panel-body">
                  {stats.topBills.length > 0 ? (
                    <div className="top-list">
                      {stats.topBills.slice(0, 5).map((bill, i) => (
                        <div key={i} className="top-item">
                          <span className="rank">{i + 1}</span>
                          <BillIcon iconUrl={bill.iconUrl} categoryIcon={getCategoryInfo(bill.category).icon} billName={bill.name} size="sm" />
                          <div className="top-info">
                            <span className="top-name">{bill.name}</span>
                            <span className="top-count">{bill.count} payments</span>
                          </div>
                          <span className="top-amount">{formatAmount(bill.total)}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty">No expense data</div>
                  )}
                </div>
              </section>
            </div>
          </>
        )}

        {viewMode === 'monthly' && (
          <section className="panel full">
            <h3><BarChart3 size={18} /> Monthly Breakdown - {selectedYear}</h3>
            <div className="panel-body">
              <BarChart data={stats.monthlyData} height={300} currencySymbol={currencySymbol} />
              <table className="monthly-table">
                <thead>
                  <tr><th>Month</th><th>Amount</th><th>vs Avg</th><th>% of Year</th></tr>
                </thead>
                <tbody>
                  {stats.monthlyData.map((m, i) => (
                    <tr key={i} className={m.value === 0 ? 'muted' : ''}>
                      <td>{m.label}</td>
                      <td>{formatAmount(m.value)}</td>
                      <td className={m.value > stats.avgMonthly ? 'red' : 'green'}>
                        {m.value > 0 ? `${m.value > stats.avgMonthly ? '+' : ''}${formatAmount(m.value - stats.avgMonthly)}` : '-'}
                      </td>
                      <td>{stats.totalSpent > 0 ? `${((m.value / stats.totalSpent) * 100).toFixed(1)}%` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        )}

        {viewMode === 'category' && (
          <div className="panel-row">
            <section className="panel">
              <h3><PieChart size={18} /> Spending by Category</h3>
              <div className="panel-body center">
                {stats.categoryData.length > 0 ? (
                  <DonutChart data={stats.categoryData} size={220} currencySymbol={currencySymbol} />
                ) : (
                  <div className="empty">No category data for {selectedYear}</div>
                )}
              </div>
            </section>
            <section className="panel">
              <h3><BarChart3 size={18} /> Category Breakdown</h3>
              <div className="panel-body">
                {stats.categoryData.length > 0 ? (
                  stats.categoryData.map((cat, i) => (
                    <div key={i} className="progress-row">
                      <div className="progress-label">
                        <span>{cat.icon} {cat.label}</span>
                        <span>{formatAmount(cat.value)}</span>
                      </div>
                      <div className="progress-track">
                        <div className="progress-fill" style={{ width: `${(cat.value / stats.categoryData[0]?.value) * 100}%`, backgroundColor: cat.color }} />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="empty">No category data for {selectedYear}</div>
                )}
              </div>
            </section>
          </div>
        )}

        {viewMode === 'comparison' && (
          <>
            {/* Year-over-Year Summary - Two half-width cards */}
            {stats.yoyData.prevTotalSpent > 0 ? (
              <div className="panel-row">
                <section className="panel">
                  <h3><TrendingUp size={18} /> Total Spending</h3>
                  <div className="panel-body center">
                    <div className="yoy-values">
                      <div className="yoy-current">
                        <span className="yoy-year">{selectedYear}</span>
                        <span className="yoy-amount">{formatAmountLocale(stats.totalSpent)}</span>
                      </div>
                      <div className="yoy-arrow">
                        {stats.yoyData.totalChange !== null && (
                          stats.yoyData.totalChange > 0 
                            ? <ArrowUpRight size={24} className="up" />
                            : stats.yoyData.totalChange < 0 
                              ? <ArrowDownRight size={24} className="down" />
                              : <Minus size={24} className="neutral" />
                        )}
                      </div>
                      <div className="yoy-previous">
                        <span className="yoy-year">{selectedYear - 1}</span>
                        <span className="yoy-amount">{formatAmountLocale(stats.yoyData.prevTotalSpent)}</span>
                      </div>
                    </div>
                    {stats.yoyData.totalChange !== null && (
                      <div className={`yoy-change ${stats.yoyData.totalChange > 0 ? 'up' : stats.yoyData.totalChange < 0 ? 'down' : 'neutral'}`}>
                        {stats.yoyData.totalChange > 0 ? '+' : ''}{stats.yoyData.totalChange.toFixed(1)}%
                        <span>{stats.yoyData.totalChange > 0 ? 'increase' : stats.yoyData.totalChange < 0 ? 'decrease' : 'no change'}</span>
                      </div>
                    )}
                  </div>
                </section>
                <section className="panel">
                  <h3><Calendar size={18} /> Monthly Average</h3>
                  <div className="panel-body center">
                    <div className="yoy-values">
                      <div className="yoy-current">
                        <span className="yoy-year">{selectedYear}</span>
                        <span className="yoy-amount">{formatAmountLocale(stats.avgMonthly)}</span>
                      </div>
                      <div className="yoy-arrow">
                        {stats.yoyData.avgChange !== null && (
                          stats.yoyData.avgChange > 0 
                            ? <ArrowUpRight size={24} className="up" />
                            : stats.yoyData.avgChange < 0 
                              ? <ArrowDownRight size={24} className="down" />
                              : <Minus size={24} className="neutral" />
                        )}
                      </div>
                      <div className="yoy-previous">
                        <span className="yoy-year">{selectedYear - 1}</span>
                        <span className="yoy-amount">{formatAmountLocale(stats.yoyData.prevAvgMonthly)}</span>
                      </div>
                    </div>
                    {stats.yoyData.avgChange !== null && (
                      <div className={`yoy-change ${stats.yoyData.avgChange > 0 ? 'up' : stats.yoyData.avgChange < 0 ? 'down' : 'neutral'}`}>
                        {stats.yoyData.avgChange > 0 ? '+' : ''}{stats.yoyData.avgChange.toFixed(1)}%
                        <span>{stats.yoyData.avgChange > 0 ? 'increase' : stats.yoyData.avgChange < 0 ? 'decrease' : 'no change'}</span>
                      </div>
                    )}
                  </div>
                </section>
              </div>
            ) : (
              <section className="panel full">
                <h3><TrendingUp size={18} /> Year-over-Year Comparison</h3>
                <div className="panel-body">
                  <div className="empty">No data available for {selectedYear - 1} to compare</div>
                </div>
              </section>
            )}

            {/* Monthly YoY Comparison - Full width */}
            {stats.yoyData.prevTotalSpent > 0 && (
              <section className="panel full">
                <h3><BarChart3 size={18} /> Monthly Comparison: {selectedYear} vs {selectedYear - 1}</h3>
                <div className="panel-body">
                  <div className="yoy-monthly-chart">
                    {stats.yoyData.monthlyComparison.map((m, i) => (
                      <div key={i} className="yoy-month-column">
                        <div className="yoy-bars">
                          <div className="yoy-bar-group">
                            <div 
                              className="yoy-bar current" 
                              style={{ 
                                height: `${Math.max((m.value / Math.max(stats.totalSpent / 3, 1)) * 100, m.value > 0 ? 4 : 0)}%` 
                              }}
                              title={`${selectedYear}: ${formatAmount(m.value)}`}
                            />
                            <div 
                              className="yoy-bar previous" 
                              style={{ 
                                height: `${Math.max((m.prevValue / Math.max(stats.totalSpent / 3, 1)) * 100, m.prevValue > 0 ? 4 : 0)}%` 
                              }}
                              title={`${selectedYear - 1}: ${formatAmount(m.prevValue)}`}
                            />
                          </div>
                        </div>
                        <span className="yoy-month-label">{m.label}</span>
                        {m.change !== null && (
                          <span className={`yoy-month-change ${m.change > 0 ? 'up' : m.change < 0 ? 'down' : 'neutral'}`}>
                            {m.change > 0 ? '+' : ''}{m.change.toFixed(0)}%
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="yoy-legend">
                    <div className="yoy-legend-item">
                      <span className="yoy-legend-dot current"></span>
                      <span>{selectedYear}</span>
                    </div>
                    <div className="yoy-legend-item">
                      <span className="yoy-legend-dot previous"></span>
                      <span>{selectedYear - 1}</span>
                    </div>
                  </div>
                </div>
              </section>
            )}

            {/* Category YoY + Payment Automation - Two half-width cards */}
            <div className="panel-row">
              {stats.yoyData.prevTotalSpent > 0 && stats.yoyData.categoryComparison.length > 0 && (
                <section className="panel">
                  <h3><PieChart size={18} /> Category Changes</h3>
                  <div className="panel-body">
                    <div className="yoy-category-list">
                      {stats.yoyData.categoryComparison.slice(0, 5).map((cat, i) => (
                        <div key={i} className="yoy-category-row">
                          <div className="yoy-category-info">
                            <span className="yoy-category-icon">{cat.icon}</span>
                            <span className="yoy-category-name">{cat.label}</span>
                          </div>
                          <div className="yoy-category-values">
                            <span className="yoy-category-current">{formatAmount(cat.value)}</span>
                            {cat.change !== null ? (
                              <span className={`yoy-category-change ${cat.change > 0 ? 'up' : cat.change < 0 ? 'down' : 'neutral'}`}>
                                {cat.change > 0 ? <TrendingUp size={14} /> : cat.change < 0 ? <TrendingDown size={14} /> : <Minus size={14} />}
                                {Math.abs(cat.change).toFixed(1)}%
                              </span>
                            ) : (
                              <span className="yoy-category-change new">New</span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
              )}
              <section className="panel">
                <h3><Zap size={18} /> Payment Automation</h3>
                <div className="panel-body center">
                  <div className="auto-stats">
                    <div className="auto-item">
                      <div className="auto-icon green"><Zap size={24} /></div>
                      <strong>{stats.autoPayCount}</strong>
                      <span>Auto-Pay</span>
                    </div>
                    <div className="auto-item">
                      <div className="auto-icon gray"><CreditCard size={24} /></div>
                      <strong>{stats.paymentCount - stats.autoPayCount}</strong>
                      <span>Manual</span>
                    </div>
                  </div>
                  <div className="auto-rate">
                    <span>Automation Rate</span>
                    <strong>{stats.paymentCount > 0 ? ((stats.autoPayCount / stats.paymentCount) * 100).toFixed(1) : 0}%</strong>
                  </div>
                </div>
              </section>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default Stats;
